import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ViewIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import FilteredIcon from "@mui/icons-material/FilterAltOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NextIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import Table from "../blocks/table";
import IdColumn from "./table-columns/id-column";
import StatusColumn from "./table-columns/status-column";
import PriorityColumn from "./table-columns/priority-column";
import ContactColumn from "./table-columns/contact-column";
import ContactAddressColumn from "./table-columns/contact-address-column";
import ContactTypeColumn from "./table-columns/contact-type-column";
import ContactPhonesColumn from "./table-columns/contact-phones-column";
import ContactOibColumn from "./table-columns/contact-oib-column";
import ContactEmailsColumn from "./table-columns/contact-emails-columns";
import GroupColumn from "./table-columns/group-column";
import AssigneeColumn from "./table-columns/assignee-column";
import TicketUserColumn from "./table-columns/ticket-user-column";
import TicketDateColumn from "./table-columns/ticket-date-column";
import TicketEstimateColumn from "./table-columns/ticket-estimate-column";
import TickeProgressColumn from "./table-columns/ticket-progress-column";
import TicketNotesColumn from "./table-columns/ticket-notes-column";
import CustomFieldColumn from "./table-columns/custom-field-column";
import TicketQuickFilters from "./ticket-quick-filters";
import TicketFilters from "./ticket-filters";
import TicketExport from "./ticket-export";
import TicketViewModal from "./ticket-view-modal";
import TicketEditModal from "./ticket-edit-modal";
import ContactViewModal from "../contacts/contact-view-modal";
import TicketDetailsTable from "./ticket-details-table";
import TicketSearch from "../tickets/ticket-search";
import { prettifyCampaignDisplayOption } from "../../../helpers/clean";
import { useTenancy } from "../../../services/tenancy";
import { useRoles } from "../../../services/roles";
import { useAlert } from "../../../services/alert";
import { useAuth } from "../../../services/auth";
import { useApiTenant } from "../../../api/api-tenant";
import { defaultColumnDisplayOptions } from "../../../helpers/defaults";
import analytics from "../../../helpers/analytics";
export default function TicketsTable({ type, campaign, contact, user, group, setResetStatusAndPriorityCount, isPreview, lead, statusCount, priorityCount, setCountDisabled, displayOptions, }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tenancy = useTenancy();
    const roles = useRoles();
    const alert = useAlert();
    const auth = useAuth();
    const api = useApiTenant();
    const tableRef = useRef();
    const defaultAssignees = () => {
        let users = [];
        if ((type === "assigned" || (campaign && campaign.type === "contract" && roles.isAgent)) &&
            user) {
            users = [user];
        }
        return users;
    };
    const defaultGroups = () => {
        let groups = [];
        if (group) {
            groups = [group];
        }
        if (((process.env.APP_ID !== "mcd" && process.env.APP_ID !== "pias" && roles.isAgent) ||
            ((process.env.APP_ID === "mcd" || process.env.APP_ID === "pias") &&
                !roles.isAdmin)) &&
            (type === "user" ||
                type === "assigned" ||
                type === "scheduled" ||
                type === "drafts" ||
                type === "campaign" ||
                type === "history" ||
                type === "tickets")) {
            groups = roles.groupsTenant ? roles.groupsTenant : [];
        }
        return groups;
    };
    const defaultUngrouped = () => {
        var _a;
        let ungrouped = "include";
        if (group || (roles.isAgent && ((_a = tenancy.client) === null || _a === void 0 ? void 0 : _a.domain) === "naljepnice"))
            ungrouped = "exclude";
        return ungrouped;
    };
    const defaultUnassigned = () => {
        let unassigned = "include";
        if (type === "assigned")
            unassigned = "exclude";
        return unassigned;
    };
    const defaultStartDate = () => {
        let startDate = "";
        if (type === "scheduled") {
            let date = new Date();
            let year = date.getFullYear();
            let month = date.getMonth() + 1;
            let day = date.getDate();
            startDate = year + "-" + month + "-" + day + " 00:00:00";
        }
        return startDate;
    };
    const defaultDraft = () => {
        let drafts = "exclude";
        if (type === "drafts")
            drafts = "only";
        return drafts;
    };
    const defaultCreatedBy = () => {
        let createdBy = [];
        if ((type === "user" || type === "scheduled" || type === "drafts") && user)
            createdBy = [user];
        return createdBy;
    };
    const [tableQuery, setTableQuery] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [search, setSearch] = useState("");
    const [searchBy, setSearchBy] = useState(process.env.APP_ID === "mcd" ? "id" : "contact.phones");
    const [showSearch, setShowSearch] = useState(false);
    const [applySearch, setApplySearch] = useState(false);
    const [filterCampaigns, setFilterCampaigns] = useState(campaign ? [campaign] : []);
    const [filterContacts, setFilterContacts] = useState(contact ? [contact] : []);
    const [filterStatuses, setFilterStatuses] = useState([]);
    const [filterPriorities, setFilterPriorities] = useState([]);
    const [filterAssignees, setFilterAssignees] = useState(defaultAssignees);
    const [filterGroups, setFilterGroups] = useState(defaultGroups);
    const [filterCreatedBy, setFilterCreatedBy] = useState(defaultCreatedBy);
    const [filterUpdatedBy, setFilterUpdatedBy] = useState([]);
    const [filterDrafts, setFilterDrafts] = useState(defaultDraft);
    const [filterUnassigned, setFilterUnassigned] = useState(defaultUnassigned);
    const [filterUngrouped, setFilterUngrouped] = useState(defaultUngrouped);
    const [filterCreatedStartDate, setFilterCreatedStartDate] = useState("");
    const [filterCreatedEndDate, setCreatedFilterEndDate] = useState("");
    const [filterCreatedStartDateCustom, setFilterCreatedStartDateCustom] = useState("");
    const [filterCreatedEndDateCustom, setFilterCreatedEndDateCustom] = useState("");
    const [filterCreatedStartTimeCustom, setFilterCreatedStartTimeCustom] = useState("");
    const [filterCreatedEndTimeCustom, setFilterCreatedEndTimeCustom] = useState("");
    const [filterUpdatedStartDate, setFilterUpdatedStartDate] = useState("");
    const [filterUpdatedEndDate, setFilterUpdatedEndDate] = useState("");
    const [filterUpdatedStartTime, setFilterUpdatedStartTime] = useState("");
    const [filterUpdatedEndTime, setFilterUpdatedEndTime] = useState("");
    const [filterStartStartDate, setFilterStartStartDate] = useState(defaultStartDate);
    const [filterStartEndDate, setFilterStartEndDate] = useState("");
    const [filterStartStartTime, setFilterStartStartTime] = useState(defaultStartDate);
    const [filterStartEndTime, setFilterStartEndTime] = useState("");
    const [filterDueStartDate, setFilterDueStartDate] = useState("");
    const [filterDueEndDate, setFilterDueEndDate] = useState("");
    const [filterDueStartTime, setFilterDueStartTime] = useState("");
    const [filterDueEndTime, setFilterDueEndTime] = useState("");
    const [filterAudiences, setFilterAudiences] = useState([]);
    const [filterCustomFields, setFilterCustomFields] = useState([]);
    const [filterCampaignChoices, setFilterCampaignChoices] = useState([]);
    const [filterContactIsNew, setFilterContactIsNew] = useState("include");
    const [filterInboundChannels, setFilterInboundChannels] = useState([]);
    const [filterOutboundChannels, setFilterOutboundChannels] = useState([]);
    const [filterInboundSocialChannels, setFilterInboundSocialChannels] = useState([]);
    const [filterOutboundSocialChannels, setFilterOutboundSocialChannels] = useState([]);
    const [filterForwardedCalls, setFilterForwardedCalls] = useState("include");
    const [showMoreFilters, setShowMoreFilters] = useState(false);
    const [showFieldFilters, setShowFieldFilters] = useState(false);
    const [showChannelFilters, setShowChannelFilters] = useState(false);
    const [totalTicketsNumber, setTotalTicketsNumber] = useState(undefined);
    const [showCount, setShowCount] = useState(true);
    const [showTicketModal, setShowTicketModal] = useState(false);
    const [ticketModalId, setTicketModalId] = useState(null);
    const [showEditTicketModal, setShowEditTicketModal] = useState(false);
    const [editTicketModalId, setEditTicketModalId] = useState(null);
    const [updatedTicket, setUpdatedTicket] = useState(false);
    const [deletedTicket, setDeletedTicket] = useState(false);
    const [contactModalId, setContactModalId] = useState(null);
    const [openExportOptions, setOpenExportOptions] = useState(false);
    const [cloneId, setCloneId] = useState("");
    const [filter, setFilter] = useState(false);
    const [isModify, setIsModify] = useState(false);
    const [areFieldsAndGroupsLoaded, setAreFieldsAndGroupsLoaded] = useState(false);
    const [groups, setGroups] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [filterableChoices, setFilterableChoices] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [audiences, setAudiences] = useState([]);
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [timeFrame, setTimeFrame] = useState("total");
    const isMobile = useMediaQuery("(max-width: 767px)");
    useEffect(() => {
        return () => api.cancel();
    }, []);
    const cloneTicket = cloneId => {
        api.CLONE_ticket(cloneId, lead ? lead.id : undefined)
            .then(response => {
            alert.show(t("tickets.tickets-table.ticket-cloned"), "info");
            navigate(tenancy.link("/tickets/" + response.data.ticket.id + "/information"));
        })
            .catch(error => {
            if (!api.isCancel(error)) {
                setIsLoading(false);
                console.error(error);
                alert.show(t("tickets.tickets-table.ticket-clone-error"), "error");
            }
        });
    };
    useEffect(() => {
        if (cloneId === "")
            return;
        cloneTicket(cloneId);
        return () => api.cancel();
    }, [cloneId]);
    useEffect(() => {
        if (isLoading || (!filter && !applySearch))
            return;
        resetTablePageAndRefreshData();
        if (filter)
            setFilter(false);
        if (applySearch)
            setApplySearch(false);
        setTotalTicketsNumber(undefined);
    }, [filter, filterStatuses, filterPriorities, applySearch]);
    useEffect(() => {
        if (isLoading || (type === "contact" && isPreview) || type === "history")
            return;
        resetTablePageAndRefreshData();
        setTotalTicketsNumber(undefined);
    }, [filterStatuses, filterPriorities]);
    useEffect(() => {
        if (campaign && (filter || applySearch)) {
            if ((searchBy === "contact.phones" && search !== "") ||
                filterContacts.length > 0 ||
                (type !== "group"
                    ? !roles.isAgent
                        ? filterGroups.length > 0
                        : defaultGroups().length === 0
                            ? filterGroups.length > 0
                            : filterGroups !== defaultGroups()
                    : false) ||
                (campaign &&
                    campaign.type === "contract" &&
                    roles.isAgent &&
                    filterAssignees.length > 1) ||
                (campaign &&
                    campaign.type === "contract" &&
                    !roles.isAgent &&
                    filterAssignees.length > 0) ||
                (campaign && campaign.type !== "contract" && filterAssignees.length > 0) ||
                filterCreatedBy.length > 0 ||
                filterUpdatedBy.length > 0 ||
                filterDrafts !== "exclude" ||
                filterUnassigned !== defaultUnassigned() ||
                (type === "campaign" && filterUngrouped !== defaultUngrouped()) ||
                filterCreatedStartDate !== "" ||
                filterCreatedEndDate !== "" ||
                filterCreatedStartDateCustom !== "" ||
                filterCreatedEndDateCustom !== "" ||
                filterUpdatedStartDate !== "" ||
                filterUpdatedEndDate !== "" ||
                filterStartStartDate !== "" ||
                filterStartEndDate !== "" ||
                filterDueStartDate !== "" ||
                filterDueEndDate !== "" ||
                filterAudiences.length > 0 ||
                filterCustomFields.length > 0 ||
                filterCampaignChoices.length > 0 ||
                filterContactIsNew !== "include" ||
                filterInboundChannels.length > 0 ||
                filterOutboundChannels.length > 0 ||
                filterInboundSocialChannels.length > 0 ||
                filterOutboundSocialChannels.length > 0 ||
                filterForwardedCalls !== "include") {
                setShowCount(false);
                if (setCountDisabled)
                    setCountDisabled(true);
            }
            else {
                setShowCount(true);
                if (setCountDisabled)
                    setCountDisabled(false);
            }
        }
    }, [filter, applySearch]);
    useEffect(() => {
        if (!updatedTicket)
            return;
        if (process.env.APP_ID === "pias") {
            setShowEditTicketModal(false);
        }
        refreshTableData();
        if (setResetStatusAndPriorityCount)
            setResetStatusAndPriorityCount(true);
        setUpdatedTicket(false);
        alert.show(t("tickets.tickets-table.ticket-updated"), "success");
    }, [updatedTicket]);
    useEffect(() => {
        if (!deletedTicket)
            return;
        refreshTableData();
        if (setResetStatusAndPriorityCount)
            setResetStatusAndPriorityCount(true);
        setDeletedTicket(false);
        setShowEditTicketModal(false);
        setEditTicketModalId(null);
        alert.show(t("tickets.tickets-table.ticket-deleted"), "warning");
    }, [deletedTicket]);
    const getFieldAndChoiceFilterValues = () => {
        let fieldValues = [];
        let fieldIds = "";
        let choiceValues = [];
        let choiceIds = "";
        if (filterCustomFields.length > 0) {
            filterCustomFields.map((cf, index) => {
                let fValues = "";
                cf.chosenItems.map((item, index) => {
                    if (item !== null && item !== undefined) {
                        if (cf.customField.field_type === "slider" ||
                            cf.customField.field_type === "range" ||
                            cf.customField.field_type === "rating") {
                            fValues =
                                fValues + item + (index !== cf.chosenItems.length - 1 ? "|" : "");
                        }
                        else {
                            fValues =
                                fValues +
                                    (cf.customField.field_type !== "switch" ? item.label : item.value) +
                                    (index !== cf.chosenItems.length - 1 ? "|" : "");
                        }
                    }
                });
                fieldValues.push(fValues);
                fieldIds =
                    fieldIds +
                        cf.customFieldId +
                        (index !== filterCustomFields.length - 1 ? "," : "");
            });
        }
        if (filterCampaignChoices.length > 0) {
            filterCampaignChoices.map((choice, index) => {
                let cValues = "";
                choice.chosenItems.map((item, index) => {
                    if (item.name) {
                        cValues =
                            cValues +
                                item.name +
                                (index !== choice.chosenItems.length - 1 ? "|" : "");
                    }
                });
                choiceValues.push(cValues);
                choiceIds =
                    choiceIds +
                        choice.campaign_step_id +
                        (index !== filterCampaignChoices.length - 1 ? "," : "");
            });
        }
        return { fieldValues, fieldIds, choiceValues, choiceIds };
    };
    const getTableData = query => {
        setTableQuery(query);
        const { fieldValues, fieldIds, choiceValues, choiceIds } = getFieldAndChoiceFilterValues();
        return new Promise(resolve => {
            setIsLoading(true);
            api.GET_tickets(query.pageSize, query.page + 1, "id", "desc", search !== "" ? search : undefined, search !== "" ? searchBy : undefined, filterCampaigns.length > 0
                ? filterCampaigns.map(campaign => campaign.id)
                : undefined, filterContacts.length > 0 ? filterContacts.map(contact => contact.id) : undefined, filterStatuses.length > 0 ? filterStatuses.map(status => status.id) : undefined, filterPriorities.length > 0
                ? filterPriorities.map(priority => priority.id)
                : undefined, filterAssignees.length > 0 ? filterAssignees.map(user => user.id) : undefined, filterGroups.length > 0
                ? filterGroups.map(group => group.id)
                : !roles.isAgent
                    ? ["-1"]
                    : undefined, filterCreatedBy.length > 0 ? filterCreatedBy.map(user => user.id) : undefined, filterUpdatedBy.length > 0 ? filterUpdatedBy.map(user => user.id) : undefined, filterDrafts, filterUnassigned, filterUngrouped, filterCreatedStartDate !== "" || filterCreatedStartDateCustom !== ""
                ? timeFrame === "custom"
                    ? filterCreatedStartTimeCustom !== ""
                        ? format(new Date(filterCreatedStartDateCustom), "yyyy-MM-dd") +
                            " " +
                            format(new Date(filterCreatedStartTimeCustom), "HH:mm:ss")
                        : format(new Date(filterCreatedStartDateCustom), "yyyy-MM-dd") +
                            " 00:00:00"
                    : filterCreatedStartDate
                : undefined, filterCreatedEndDate !== "" || filterCreatedEndDateCustom !== ""
                ? timeFrame === "custom"
                    ? filterCreatedEndTimeCustom !== ""
                        ? format(new Date(filterCreatedEndDateCustom), "yyyy-MM-dd") +
                            " " +
                            format(new Date(filterCreatedEndTimeCustom), "HH:mm:ss")
                        : format(new Date(filterCreatedEndDateCustom), "yyyy-MM-dd") +
                            " 23:59:59"
                    : filterCreatedEndDate
                : undefined, filterUpdatedStartDate !== ""
                ? filterUpdatedStartTime !== ""
                    ? format(new Date(filterUpdatedStartDate), "yyyy-MM-dd") +
                        " " +
                        format(new Date(filterUpdatedStartTime), "HH:mm:ss")
                    : format(new Date(filterUpdatedStartDate), "yyyy-MM-dd") + " 00:00:00"
                : undefined, filterUpdatedEndDate !== ""
                ? filterUpdatedEndTime !== ""
                    ? format(new Date(filterUpdatedEndDate), "yyyy-MM-dd") +
                        " " +
                        format(new Date(filterUpdatedEndTime), "HH:mm:ss")
                    : format(new Date(filterUpdatedEndDate), "yyyy-MM-dd") + " 23:59:59"
                : undefined, filterStartStartDate !== ""
                ? filterStartStartTime !== ""
                    ? format(new Date(filterStartStartDate), "yyyy-MM-dd") +
                        " " +
                        format(new Date(filterStartStartTime), "HH:mm:ss")
                    : format(new Date(filterStartStartDate), "yyyy-MM-dd") + " 00:00:00"
                : undefined, filterStartEndDate !== ""
                ? filterStartEndTime !== ""
                    ? format(new Date(filterStartEndDate), "yyyy-MM-dd") +
                        " " +
                        format(new Date(filterStartEndTime), "HH:mm:ss")
                    : format(new Date(filterStartEndDate), "yyyy-MM-dd") + " 23:59:59"
                : undefined, filterDueStartDate !== ""
                ? filterDueStartTime !== ""
                    ? format(new Date(filterDueStartDate), "yyyy-MM-dd") +
                        " " +
                        format(new Date(filterDueStartTime), "HH:mm:ss")
                    : format(new Date(filterDueStartDate), "yyyy-MM-dd") + " 00:00:00"
                : undefined, filterDueEndDate !== ""
                ? filterDueEndTime !== ""
                    ? format(new Date(filterDueEndDate), "yyyy-MM-dd") +
                        " " +
                        format(new Date(filterDueEndTime), "HH:mm:ss")
                    : format(new Date(filterDueEndDate), "yyyy-MM-dd") + " 23:59:59"
                : undefined, undefined, 0, filterAudiences.length > 0
                ? filterAudiences.map(audience => audience.id)
                : undefined, fieldIds !== "" ? fieldIds : undefined, fieldValues.length > 0 ? fieldValues : undefined, choiceIds !== "" ? choiceIds : undefined, choiceValues.length > 0 ? choiceValues : undefined, filterContactIsNew, filterInboundChannels.length > 0 ? filterInboundChannels : undefined, filterOutboundChannels.length > 0 ? filterOutboundChannels : undefined, filterInboundSocialChannels.length > 0 ? filterInboundSocialChannels : undefined, filterOutboundSocialChannels.length > 0 ? filterOutboundSocialChannels : undefined, filterForwardedCalls, campaign
                ? "customizable_campaign"
                : displayOptions
                    ? "customizable_dashboard"
                    : "customizable_default")
                .then(response => {
                let total = response.data.tickets.length === parseInt(response.data.meta.per_page)
                    ? 100000000
                    : response.data.meta.current_page === 1
                        ? response.data.tickets.length
                        : response.data.tickets.length +
                            parseInt(response.data.meta.per_page) *
                                (response.data.meta.current_page - 1);
                resolve({
                    data: response.data.tickets,
                    page: response.data.meta.current_page - 1,
                    totalCount: response.data.tickets.length === 0 ? total + 1 : total,
                });
                if (showFilters)
                    setShowFilters(false);
                if (showSearch)
                    setShowSearch(false);
                if (isModify)
                    setIsModify(false);
                setIsLoading(false);
            })
                .catch(error => {
                if (!api.isCancel(error)) {
                    console.error(error);
                    setIsLoading(false);
                }
            });
        });
    };
    const refreshTableData = () => {
        var _a;
        // @ts-ignore
        (_a = tableRef === null || tableRef === void 0 ? void 0 : tableRef.current) === null || _a === void 0 ? void 0 : _a.onQueryChange();
        if (setResetStatusAndPriorityCount)
            setResetStatusAndPriorityCount(true);
        setTicketModalId(null);
    };
    const resetTablePageAndRefreshData = () => {
        var _a;
        setIsLoading(true);
        // @ts-ignore
        (_a = tableRef === null || tableRef === void 0 ? void 0 : tableRef.current) === null || _a === void 0 ? void 0 : _a.onChangePage(null, 0);
    };
    const assignLeadAndRedirect = (ticketId, lead) => {
        setIsLoading(true);
        api.UPDATE_ticket(ticketId, lead.id)
            .then(() => {
            campaign && (campaign === null || campaign === void 0 ? void 0 : campaign.has_steps)
                ? navigate(tenancy.link("/tickets/" + ticketId + "/information"))
                : navigate(tenancy.link("/tickets/" + ticketId + "/details"));
        })
            .catch(error => {
            console.error(error);
        });
        setIsLoading(false);
        return () => api.cancel();
    };
    const shouldColumnBeHidden = (columnDisplayOption) => {
        if (columnDisplayOption.relation == "contact" &&
            columnDisplayOption.column == null &&
            (type === "contact" || type === "history")) {
            return true;
        }
        else
            return !!(columnDisplayOption.relation == "groups" &&
                (group ||
                    (type === "campaign" &&
                        campaign &&
                        campaign.groups &&
                        campaign.groups.length === 0)));
    };
    const tableTitle = type === "campaign"
        ? t("tickets.campaign-tickets")
        : type === "contact"
            ? t("tickets.contact-tickets")
            : type === "history"
                ? t("tickets.contact-tickets")
                : type === "group"
                    ? t("tickets.queue-tickets")
                    : type === "user"
                        ? t("tickets.tickets-table.user-tickets")
                        : type === "assigned"
                            ? t("tickets.tickets-table.assigned-tickets")
                            : type === "scheduled"
                                ? t("tickets.tickets-table.scheduled-tickets")
                                : type === "drafts"
                                    ? t("tickets.tickets-table.draft-tickets")
                                    : t("tickets.tickets-table.all-tickets");
    const tableActions = [
        type === "history" && !isPreview && campaign && campaign.has_steps
            ? ticket => ({
                icon: () => _jsx(ContentCopyIcon, { color: ticket.is_draft ? "disabled" : "primary" }),
                disabled: ticket.is_draft,
                tooltip: t("tickets.tickets-table.duplicate-this-ticket"),
                onClick: () => {
                    setCloneId(ticket.id);
                    setIsLoading(true);
                },
            })
            : undefined,
        type === "history" && !isPreview
            ? {
                icon: () => (lead ? _jsx(NextIcon, { color: "primary" }) : _jsx(EditIcon, { color: "primary" })),
                tooltip: lead
                    ? t("tickets.tickets-table.continue-with-this-ticket")
                    : t("tickets.tickets-table.edit-this-ticket"),
                onClick: (event, ticket) => {
                    if (lead) {
                        assignLeadAndRedirect(ticket.id, lead);
                    }
                    else {
                        campaign && (campaign === null || campaign === void 0 ? void 0 : campaign.has_steps)
                            ? navigate(tenancy.link("/tickets/" + ticket.id + "/information"))
                            : navigate(tenancy.link("/tickets/" + ticket.id + "/details"));
                    }
                },
            }
            : undefined,
        type !== "history" && !(type == "contact" && isPreview)
            ? {
                icon: () => _jsx(ViewIcon, { color: "action" }),
                tooltip: t("tickets.view-ticket"),
                onClick: (event, ticket) => {
                    setTicketModalId(ticket.id);
                    setShowTicketModal(true);
                    setEditTicketModalId(null);
                    analytics.sendModalView("/ticket/" + ticket.id + "/contact", auth.user);
                },
            }
            : undefined,
        type !== "history"
            ? ticket => ({
                icon: () => ticket.is_draft || (type === "contact" && isPreview) ? (_jsx(NextIcon, { color: "action" })) : (_jsx(EditIcon, { color: "action" })),
                tooltip: ticket.is_draft
                    ? t("tickets.complete-ticket")
                    : type === "contact" && isPreview
                        ? t("tickets.view-ticket")
                        : t("tickets.edit-ticket"),
                onClick: () => {
                    if (ticket.is_draft) {
                        window.open(tenancy.link("/tickets/" + ticket.id + "/contact"));
                    }
                    else if (type === "contact" && isPreview) {
                        window.open(tenancy.link("/tickets/" + ticket.id));
                    }
                    else {
                        setEditTicketModalId(ticket.id);
                        setShowEditTicketModal(true);
                        setTicketModalId(null);
                        analytics.sendModalView("/ticket/" + ticket.id + "/contact/edit", auth.user);
                    }
                },
            })
            : undefined,
        {
            icon: () => _jsx(SearchIcon, { color: "action" }),
            tooltip: t("search.search") + " " + t("search.tickets"),
            isFreeAction: true,
            onClick: () => setShowSearch(true),
        },
        {
            icon: () => _jsx(FilteredIcon, { color: "action" }),
            tooltip: t("tickets.tickets-table.filter-tickets"),
            isFreeAction: true,
            onClick: () => setShowFilters(true),
        },
        campaign && (roles.isAdmin || roles.isManager)
            ? {
                icon: () => isExporting ? (_jsx(Box, Object.assign({ sx: {
                        paddingX: "3px",
                    } }, { children: _jsx(CircularProgress, { color: "inherit", size: 18 }) }))) : (_jsx(DownloadIcon, {})),
                tooltip: t("tickets.tickets-table.export-tickets"),
                isFreeAction: true,
                onClick: () => {
                    setOpenExportOptions(true);
                },
            }
            : undefined,
        {
            icon: () => _jsx(RefreshIcon, { color: "action" }),
            tooltip: t("tickets.tickets-table.refresh-tickets"),
            isFreeAction: true,
            onClick: () => refreshTableData(),
        },
    ];
    const columnDisplayOptions = campaign
        ? campaign.column_display_options
        : displayOptions !== null && displayOptions !== void 0 ? displayOptions : defaultColumnDisplayOptions;
    const tableColumns = isMobile
        ? [
            {
                title: process.env.APP_ID === "mcd" ? t("tickets.code") : t("tickets.ticket") + " #",
                field: "id",
                sorting: false,
            },
        ]
        : columnDisplayOptions.map((columnDisplayOption, index) => {
            return {
                title: prettifyCampaignDisplayOption(columnDisplayOption, t, false, false),
                sorting: false,
                type: columnDisplayOption.column === "id" ? "numeric" : "string",
                maxWidth: columnDisplayOption.column === "id" ? "110px" : undefined,
                hidden: shouldColumnBeHidden(columnDisplayOption),
                render: (ticket) => {
                    return columnDisplayOption.relation == "ticketDetail.status" ? (_jsx(StatusColumn, { type: type, ticket: ticket, ticketStatus: ticket.customizable_resources[index]["ticketDetail.status"], isPreview: isPreview, refreshTableData: refreshTableData })) : columnDisplayOption.relation == "ticketDetail.priority" ? (_jsx(PriorityColumn, { type: type, ticket: ticket, ticketPriority: ticket.customizable_resources[index]["ticketDetail.priority"], isPreview: isPreview, refreshTableData: refreshTableData })) : columnDisplayOption.relation == null &&
                        columnDisplayOption.column == "id" ? (_jsx(IdColumn, { ticket: ticket })) : columnDisplayOption.relation == "contact" &&
                        (columnDisplayOption.column == "company" ||
                            columnDisplayOption.column == "first_name" ||
                            columnDisplayOption.column == "last_name") ? (_jsx(Typography, { children: ticket.customizable_resources[index] })) : columnDisplayOption.relation == "contact" &&
                        columnDisplayOption.column == null &&
                        !columnDisplayOption.custom_field &&
                        !columnDisplayOption.campaign_step ? (_jsx(ContactColumn, { contact: ticket.customizable_resources[index]["contact"], setContactModalOpen: setContactModalOpen, setContactModalId: setContactModalId, displayOption: columnDisplayOption })) : (columnDisplayOption.relation == null &&
                        (columnDisplayOption.column == "created_at" ||
                            columnDisplayOption.column == "updated_at")) ||
                        (columnDisplayOption.relation == "ticketDetail" &&
                            (columnDisplayOption.column == "due_date_time" ||
                                columnDisplayOption.column == "start_date_time")) ? (_jsx(TicketDateColumn, { date: ticket.customizable_resources &&
                            ticket.customizable_resources[index]
                            ? ticket.customizable_resources[index]
                            : ticket.created_at })) : columnDisplayOption.relation == "ticketDetail" &&
                        columnDisplayOption.column == "estimate" ? (_jsx(TicketEstimateColumn, { estimate: ticket.customizable_resources[index] })) : columnDisplayOption.relation == "ticketDetail" &&
                        columnDisplayOption.column == "progress" ? (_jsx(TickeProgressColumn, { progress: ticket.customizable_resources[index] })) : columnDisplayOption.relation == null &&
                        columnDisplayOption.column == "created_at" ? (_jsx(Typography, { children: new Date(Date.parse(ticket.created_at)).toLocaleDateString("en-GB") +
                            " " +
                            new Date(Date.parse(ticket.created_at)).toLocaleTimeString("hr-HR", {
                                hour: "2-digit",
                                minute: "2-digit",
                            }) })) : columnDisplayOption.relation == "ticketDetail" &&
                        columnDisplayOption.column == "forwarded_call" ? (ticket.customizable_resources[index] ? (t("yes")) : (t("no"))) : columnDisplayOption.relation == "ticketDetail" &&
                        columnDisplayOption.column == "notes" ? (_jsx(TicketNotesColumn, { notes: ticket.customizable_resources[index] })) : columnDisplayOption.relation == "groups" ? (_jsx(GroupColumn, { ticket: ticket })) : columnDisplayOption.relation == "ticketDetail.assignees" ? (_jsx(AssigneeColumn, { type: type, ticket: ticket, ticketAssignees: ticket.customizable_resources[index]["ticketDetail.assignees"], isPreview: isPreview, refreshTableData: refreshTableData })) : columnDisplayOption.relation == "contact" &&
                        columnDisplayOption.column == "type" ? (_jsx(ContactTypeColumn, { type: ticket.customizable_resources[index] })) : columnDisplayOption.relation == "contact.phones" ? (_jsx(ContactPhonesColumn, { phones: ticket.customizable_resources[index]["contact.phones"] })) : columnDisplayOption.relation == "contact.address" ? (_jsx(ContactAddressColumn, { address: ticket.customizable_resources[index]["contact.address"] })) : columnDisplayOption.column == "oib" ? (_jsx(ContactOibColumn, { oib: ticket.customizable_resources[index] })) : columnDisplayOption.relation == "contact.contactEmails" ? (_jsx(ContactEmailsColumn, { emails: ticket.customizable_resources[index]["contact.contactEmails"] })) : columnDisplayOption.relation == "createdBy" ? (_jsx(TicketUserColumn, { user: ticket.customizable_resources[index]["createdBy"] })) : columnDisplayOption.relation == "updatedBy" ? (_jsx(TicketUserColumn, { user: ticket.customizable_resources[index]["updatedBy"] })) : (_jsx(CustomFieldColumn, { displayOption: columnDisplayOption, customFieldValue: ticket.customizable_resources[index] }));
                },
            };
        });
    const tableDetails = (ticket) => {
        return (_jsx(Box, Object.assign({ sx: {
                paddingX: isMobile ? 2 : 6,
                paddingY: 3,
                maxWidth: isMobile ? "inherit" : "79vw",
            } }, { children: _jsx(TicketDetailsTable, { ticketId: ticket.id, campaign: campaign, type: type, isPreview: isPreview, refreshTableData: refreshTableData, setTicketModalId: setTicketModalId }) })));
    };
    return (_jsxs(Fragment, { children: [campaign && (_jsx(TicketQuickFilters, { campaign: campaign, isLoading: isLoading, filterStatuses: filterStatuses, setFilterStatuses: setFilterStatuses, filterPriorities: filterPriorities, setFilterPriorities: setFilterPriorities, statusCount: statusCount, priorityCount: priorityCount, showCount: showCount })), _jsx(Table, { title: tableTitle, actions: tableActions, columns: tableColumns, detailPanel: process.env.APP_ID === "mcd" && !roles.isAdmin ? null : tableDetails, data: query => getTableData(query), tableRef: tableRef, isLoading: isLoading, tableQuery: tableQuery, model: type === "history", simplePagination: true, total: totalTicketsNumber, disableSearch: true }), _jsx(TicketSearch, { search: search, setSearch: setSearch, showSearch: showSearch, searchBy: searchBy, setSearchBy: setSearchBy, isLoading: isLoading, setApplySearch: setApplySearch }), _jsx(TicketFilters, { type: type, showFilters: showFilters, campaign: campaign, group: group, filterCampaigns: filterCampaigns, setFilterCampaigns: setFilterCampaigns, filterContacts: filterContacts, setFilterContacts: setFilterContacts, filterStatuses: filterStatuses, setFilterStatuses: setFilterStatuses, filterPriorities: filterPriorities, setFilterPriorities: setFilterPriorities, filterAssignees: filterAssignees, setFilterAssignees: setFilterAssignees, filterGroups: filterGroups, setFilterGroups: setFilterGroups, filterCreatedBy: filterCreatedBy, setFilterCreatedBy: setFilterCreatedBy, filterUpdatedBy: filterUpdatedBy, setFilterUpdatedBy: setFilterUpdatedBy, filterDrafts: filterDrafts, setFilterDrafts: setFilterDrafts, filterUnassigned: filterUnassigned, setFilterUnassigned: setFilterUnassigned, filterUngrouped: filterUngrouped, setFilterUngrouped: setFilterUngrouped, filterCreatedStartDate: filterCreatedStartDate, setFilterCreatedStartDate: setFilterCreatedStartDate, filterCreatedEndDate: filterCreatedEndDate, setCreatedFilterEndDate: setCreatedFilterEndDate, filterCreatedStartDateCustom: filterCreatedStartDateCustom, setFilterCreatedStartDateCustom: setFilterCreatedStartDateCustom, filterCreatedEndDateCustom: filterCreatedEndDateCustom, setFilterCreatedEndDateCustom: setFilterCreatedEndDateCustom, filterCreatedStartTimeCustom: filterCreatedStartTimeCustom, setFilterCreatedStartTimeCustom: setFilterCreatedStartTimeCustom, filterCreatedEndTimeCustom: filterCreatedEndTimeCustom, setFilterCreatedEndTimeCustom: setFilterCreatedEndTimeCustom, filterUpdatedStartDate: filterUpdatedStartDate, setFilterUpdatedStartDate: setFilterUpdatedStartDate, filterUpdatedStartTime: filterUpdatedStartTime, setFilterUpdatedStartTime: setFilterUpdatedStartTime, filterUpdatedEndDate: filterUpdatedEndDate, setFilterUpdatedEndDate: setFilterUpdatedEndDate, filterUpdatedEndTime: filterUpdatedEndTime, setFilterUpdatedEndTime: setFilterUpdatedEndTime, filterStartStartDate: filterStartStartDate, setFilterStartStartDate: setFilterStartStartDate, filterStartStartTime: filterStartStartTime, setFilterStartStartTime: setFilterStartStartTime, filterStartEndDate: filterStartEndDate, setFilterStartEndDate: setFilterStartEndDate, filterStartEndTime: filterStartEndTime, setFilterStartEndTime: setFilterStartEndTime, filterDueStartDate: filterDueStartDate, setFilterDueStartDate: setFilterDueStartDate, filterDueStartTime: filterDueStartTime, setFilterDueStartTime: setFilterDueStartTime, filterDueEndDate: filterDueEndDate, setFilterDueEndDate: setFilterDueEndDate, filterDueEndTime: filterDueEndTime, setFilterDueEndTime: setFilterDueEndTime, filterAudiences: filterAudiences, setFilterAudiences: setFilterAudiences, filterCustomFields: filterCustomFields, setFilterCustomFields: setFilterCustomFields, filterCampaignChoices: filterCampaignChoices, setFilterCampaignChoices: setFilterCampaignChoices, filterContactIsNew: filterContactIsNew, setFilterContactIsNew: setFilterContactIsNew, filterInboundChannels: filterInboundChannels, setFilterInboundChannels: setFilterInboundChannels, filterOutboundChannels: filterOutboundChannels, setFilterOutboundChannels: setFilterOutboundChannels, filterInboundSocialChannels: filterInboundSocialChannels, setFilterInboundSocialChannels: setFilterInboundSocialChannels, filterOutboundSocialChannels: filterOutboundSocialChannels, setFilterOutboundSocialChannels: setFilterOutboundSocialChannels, filterForwardedCalls: filterForwardedCalls, setFilterForwardedCalls: setFilterForwardedCalls, timeFrame: timeFrame, setTimeFrame: setTimeFrame, user: user, isLoading: isLoading, setFilter: setFilter, showMoreFilters: showMoreFilters, setShowMoreFilters: setShowMoreFilters, showFieldFilters: showFieldFilters, setShowFieldFilters: setShowFieldFilters, showChannelFilters: showChannelFilters, setShowChannelFilters: setShowChannelFilters, areFieldsAndGroupsLoaded: areFieldsAndGroupsLoaded, setAreFieldsAndGroupsLoaded: setAreFieldsAndGroupsLoaded, filterableChoices: filterableChoices, setFilterableChoices: setFilterableChoices, customFields: customFields, setCustomFields: setCustomFields, groups: groups, setGroups: setGroups, campaigns: campaigns, setCampaigns: setCampaigns, audiences: audiences, setAudiences: setAudiences }), _jsx(TicketExport, { openExportOptions: openExportOptions, setOpenExportOptions: setOpenExportOptions, campaign: campaign, filterCreatedStartDate: filterCreatedStartDate, filterCreatedEndDate: filterCreatedEndDate, filterCreatedStartDateCustom: filterCreatedStartDateCustom, filterCreatedEndDateCustom: filterCreatedEndDateCustom, filterCreatedStartTimeCustom: filterCreatedStartTimeCustom, filterCreatedEndTimeCustom: filterCreatedEndTimeCustom, isExporting: isExporting, setIsExporting: setIsExporting, getFieldAndChoiceFilterValues: getFieldAndChoiceFilterValues, search: search, searchBy: searchBy, filterCampaigns: filterCampaigns, filterContacts: filterContacts, filterStatuses: filterStatuses, filterPriorities: filterPriorities, filterAssignees: filterAssignees, filterGroups: filterGroups, filterCreatedBy: filterCreatedBy, filterUpdatedBy: filterUpdatedBy, filterDrafts: filterDrafts, filterUnassigned: filterUnassigned, filterUngrouped: filterUngrouped, timeFrame: timeFrame, filterUpdatedStartDate: filterUpdatedStartDate, filterUpdatedEndDate: filterUpdatedEndDate, filterUpdatedStartTime: filterUpdatedStartTime, filterUpdatedEndTime: filterUpdatedEndTime, filterStartStartDate: filterStartStartDate, filterStartStartTime: filterStartStartTime, filterStartEndDate: filterStartEndDate, filterStartEndTime: filterStartEndTime, filterDueStartDate: filterDueStartDate, filterDueEndDate: filterDueEndDate, filterDueStartTime: filterDueStartTime, filterDueEndTime: filterDueEndTime, filterAudiences: filterAudiences, filterContactIsNew: filterContactIsNew, filterInboundChannels: filterInboundChannels, filterOutboundChannels: filterOutboundChannels, filterInboundSocialChannels: filterInboundSocialChannels, filterOutboundSocialChannels: filterOutboundSocialChannels, filterForwardedCalls: filterForwardedCalls, setShowFilters: setShowFilters, tableQuery: tableQuery, setIsModify: setIsModify }), _jsx(TicketViewModal, { showTicketModal: showTicketModal, setShowTicketModal: setShowTicketModal, ticketModalId: ticketModalId, setDeletedTicket: setDeletedTicket }), _jsx(TicketEditModal, { showEditTicketModal: showEditTicketModal, setShowEditTicketModal: setShowEditTicketModal, editTicketModalId: editTicketModalId, setEditTicketModalId: setEditTicketModalId, updatedTicket: updatedTicket, setUpdatedTicket: setUpdatedTicket }), _jsx(ContactViewModal, { contactModalOpen: contactModalOpen, setContactModalOpen: setContactModalOpen, contactModalId: contactModalId, type: "ticket", campaign: campaign })] }));
}
